import { ref, computed } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { useCompanyStore } from '@/stores/company'
import { FirebaseProvider } from '@/infrastructure/providers/FirebaseProvider'
import { doc, getDoc } from 'firebase/firestore'
import type {
  ViewerModuleId,
  ViewerSectionConfig,
  ViewerViewConfig,
  ShipmentsMainInfoSection,
  ShipmentsMilestonesSection
} from '@/domain/types/configs/company/ViewerViewConfig'
import { normalizeViewerViewConfig } from '@/domain/types/configs/company/ViewerViewConfig'

/**
 * Estado compartido por todos los consumidores. El guard del router y el menú
 * lateral dependen de esta configuración, así que no puede haber una copia por
 * instancia del composable ni una carga por componente.
 */
const config = ref<ViewerViewConfig>(normalizeViewerViewConfig(null))
const loading = ref(false)
const loaded = ref(false)
let loadedCompanyId: string | null = null
let pendingLoad: Promise<void> | null = null

const MAIN_INFO_SECTIONS: string[] = [
  'references', 'transportDocs', 'transportConfig', 'incoterm', 'carriers'
] satisfies ShipmentsMainInfoSection[]

const MILESTONES_SECTIONS: string[] = [
  'requestedDates', 'estimatedTimes', 'actualEvents', 'travelInfo', 'lastMile'
] satisfies ShipmentsMilestonesSection[]

/**
 * Módulo de permisos (el de MODULE_ACTION_MATRIX) → módulo de la configuración
 * de solo vista. Este mapa es además la lista blanca del viewer: un módulo que
 * no esté aquí le queda negado, porque no hay forma de configurar qué ve dentro
 * de él.
 *
 * 'tracking' no está a propósito, para que Tracking Universal no aparezca en el
 * menú del viewer. El detalle /dashboard/tracking-results sigue abierto: esa
 * ruta pertenece a 'shipments' y a 'tracking', y basta uno para entrar.
 */
const VIEWER_MODULE_BY_PERMISSION: Record<string, ViewerModuleId> = {
  shipments: 'shipments',
  'purchase-orders': 'purchaseOrders'
}

/** Tab que hay que tener visible para poder subir archivos, según el módulo. */
const FILES_TAB: Record<ViewerModuleId, string> = {
  shipments: 'files',
  purchaseOrders: 'documents'
}

export const viewerModuleFor = (permissionModule: string): ViewerModuleId | null =>
  VIEWER_MODULE_BY_PERMISSION[permissionModule] ?? null

export const viewerModuleConfig = (moduleId: ViewerModuleId) => config.value.modules[moduleId]

export const invalidateViewerConfigCache = () => {
  loadedCompanyId = null
  pendingLoad = null
  loaded.value = false
  config.value = normalizeViewerViewConfig(null)
}

const isViewerUser = (): boolean => useAuthStore().user?.clientType === 'view'

const currentCompanyId = (): string | null =>
  useCompanyStore().selectedCompanyId || localStorage.getItem('selectedCompanyId')

const fetchConfig = async (companyId: string) => {
  loading.value = true
  try {
    const db = FirebaseProvider.getInstance().getFirestore()
    const snap = await getDoc(doc(db, `Companies/${companyId}/config/VIEWER_VIEW_CONFIG`))
    config.value = normalizeViewerViewConfig(snap.exists() ? snap.data() : null)
    loadedCompanyId = companyId
  } catch (error) {
    console.warn('Error cargando VIEWER_VIEW_CONFIG:', error)
  } finally {
    loading.value = false
    loaded.value = true
  }
}

/**
 * Carga la configuración una sola vez por compañía. Las llamadas concurrentes
 * comparten la misma promesa, de modo que el primer render no dispara un getDoc
 * por consumidor. Es idempotente: el guard del router puede esperarla siempre.
 */
export const ensureViewerConfigLoaded = async (): Promise<void> => {
  if (!isViewerUser()) return

  const companyId = currentCompanyId()
  if (!companyId) return

  if (loadedCompanyId !== null && loadedCompanyId !== companyId) invalidateViewerConfigCache()
  if (loaded.value && loadedCompanyId === companyId) return

  if (!pendingLoad) {
    pendingLoad = fetchConfig(companyId).finally(() => { pendingLoad = null })
  }
  await pendingLoad
}

/**
 * El módulo se liga en la construcción a propósito. Con un parámetro por
 * resolver, olvidarlo en un componente de órdenes de compra evaluaría la
 * sección contra el mapa de embarques y devolvería "visible, no editable":
 * parecería funcionar mientras expone campos que el administrador ocultó.
 */
export function useViewerConfig(moduleId: ViewerModuleId = 'shipments') {
  const isViewer = computed(() => useAuthStore().user?.clientType === 'view')

  /** La configuración ya se puede consultar: para un no-viewer nunca hay nada que esperar. */
  const isReady = computed(() => !isViewer.value || loaded.value)

  const moduleConfig = computed(() => config.value.modules[moduleId])

  const loadViewerConfig = () => ensureViewerConfigLoaded()

  const isModuleEnabled = (): boolean => !isViewer.value || moduleConfig.value.enabled

  /**
   * Sección efectiva de la configuración. `null` = sección no configurable,
   * que por convención es visible y no editable, igual que hoy son `customs` y
   * `quotation` en embarques.
   */
  const resolveSection = (sectionKey: string): ViewerSectionConfig | null => {
    const sections = moduleConfig.value.sections as Record<string, any>

    if (moduleId === 'purchaseOrders') {
      if (sectionKey === 'crossModule') {
        return (moduleConfig.value as { crossModule: ViewerSectionConfig }).crossModule
      }
      return sections[sectionKey] ?? null
    }

    if (sectionKey === 'header' || sectionKey === 'summary') return sections.summary
    if (sectionKey === 'additionalInfo') return sections.additionalInfo

    const parentKey = MAIN_INFO_SECTIONS.includes(sectionKey)
      ? 'mainInfo'
      : MILESTONES_SECTIONS.includes(sectionKey) ? 'milestones' : null
    if (!parentKey) return null

    const parent = sections[parentKey]
    if (!parent?.visible) return { visible: false }
    return parent.subSections?.[sectionKey] ?? null
  }

  const isSectionVisible = (sectionKey: string): boolean => {
    if (!isViewer.value) return true
    const section = resolveSection(sectionKey)
    return section ? section.visible : true
  }

  const isFieldVisibleBySection = (sectionKey: string, fieldId: string): boolean => {
    if (!isViewer.value) return true

    const section = resolveSection(sectionKey)
    if (!section) return true
    if (!section.visible) return false
    // Sin lista de campos, la sección entera es visible.
    if (!section.fields?.length) return true

    const field = section.fields.find(f => f.fieldId === fieldId)
    return field ? field.visible : true
  }

  const isFieldEditableBySection = (sectionKey: string, fieldId: string): boolean => {
    // No es viewer: la editabilidad la deciden los permisos del rol.
    if (!isViewer.value) return true
    // El encabezado del embarque nunca es editable por un viewer.
    if (moduleId === 'shipments' && sectionKey === 'header') return false
    if (!isFieldVisibleBySection(sectionKey, fieldId)) return false

    const section = resolveSection(sectionKey)
    if (!section?.fields?.length) return false

    const field = section.fields.find(f => f.fieldId === fieldId)
    return field ? field.editable : false
  }

  const isTabVisible = (tabKey: string): boolean => {
    if (!isViewer.value) return true
    return (moduleConfig.value.tabs as Record<string, boolean>)[tabKey] !== false
  }

  /** Tipos de documento permitidos. Vacío = sin restricción. */
  const getViewerAllowedDocumentTypes = (): string[] => {
    if (!isViewer.value) return []
    return moduleConfig.value.documentTypes.filter(dt => dt.visible).map(dt => dt.typeId)
  }

  const canViewerUploadFiles = (): boolean => {
    if (!isViewer.value) return false
    if (!isTabVisible(FILES_TAB[moduleId])) return false
    return moduleConfig.value.canUploadFiles === true
  }

  const canViewerComment = (): boolean => {
    if (!isViewer.value) return true
    if (!isTabVisible('comments')) return false
    return moduleConfig.value.canComment === true
  }

  return {
    config,
    moduleConfig,
    loading,
    loaded,
    isReady,
    isViewer,
    loadViewerConfig,
    isModuleEnabled,
    isSectionVisible,
    isFieldVisibleBySection,
    isFieldEditableBySection,
    isTabVisible,
    getViewerAllowedDocumentTypes,
    canViewerUploadFiles,
    canViewerComment,
    invalidateCache: invalidateViewerConfigCache
  }
}
